import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { addDoc, collection, doc, getDocs, onSnapshot, query, where, writeBatch } from "firebase/firestore";
import { ChevronRight } from "lucide-react";
import { useSearch } from "wouter";
import { customerTypeOf, paths, ROLE_RANK, type Customer } from "@clevis/shared-types";
import { Button, Card, CardTitle, ConfirmButton, Input, Label, Select, cn } from "@clevis/shared-ui";
import { db } from "../../lib/firebase";
import { call } from "../../lib/callFunction";
import { downloadCsv } from "../../lib/csv";
import { useAuth } from "../../hooks/useAuth";
import { AddressField, type Located } from "../../components/AddressField";
import { useCustomerList } from "../../hooks/useCustomerList";
import { BulkBar, ListFooter, SearchBox, SortHeader, useListView } from "../../components/ListControls";
import { useEntitlements } from "../../hooks/useEntitlements";
import { useAccess } from "../../hooks/useAccess";
import { useLeadSources } from "../../hooks/useLeadSources";
import { useCustomerTypes } from "../../hooks/useCustomerTypes";
import { CustomerDetail } from "./CustomerDetail";

type Row = Customer & { id: string; qbo?: { id?: string }; qbd?: { listId?: string } };

// A QuickBooks customer record offered by findQbCustomers.
interface QbMatch {
  qbId: string;
  displayName: string;
  company: string;
  email: string;
  phone: string;
  addressLine1: string;
  city: string;
  state: string;
  zip: string;
}

export function CustomersPage() {
  const { tenantId, role } = useAuth();
  const isAdmin = ROLE_RANK[role ?? "staff"] >= ROLE_RANK.admin;
  const customerTypes = useCustomerTypes(tenantId);
  const [typesDraft, setTypesDraft] = useState<string | null>(null);
  const [typesNote, setTypesNote] = useState("");
  const can = useAccess();
  // The same live list every customer picker uses (loaded once per session).
  const { rows: activeRows, loaded } = useCustomerList(tenantId);
  const rows = activeRows as Row[];
  const [archivedRows, setArchivedRows] = useState<Row[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [status, setStatus] = useState<"active" | "archived">("active");
  const [city, setCity] = useState("");
  const [qbFilter, setQbFilter] = useState("all");
  // Who to show: builders vs homeowners, who owes, who has an agreement, who has a partly-done job.
  const [show, setShow] = useState("all");
  const [facts, setFacts] = useState<{ builders: Set<string>; balance: Set<string>; agreement: Set<string>; partial: Set<string> } | null>(null);
  useEffect(() => {
    if (!tenantId || show === "all" || facts) return;
    let dead = false;
    void (async () => {
      const ids = (snap: { docs: Array<{ get: (k: string) => unknown }> } | null, keep: (d: { get: (k: string) => unknown }) => boolean = () => true) =>
        new Set((snap?.docs ?? []).filter(keep).map((d) => d.get("customerId") as string).filter(Boolean));
      const [projects, invoices, agreements, visits] = await Promise.all([
        getDocs(collection(db, paths.tenantProjects(tenantId))).catch(() => null),
        getDocs(query(collection(db, paths.tenantInvoices(tenantId)), where("status", "in", ["sent", "partially_paid"]))).catch(() => null),
        getDocs(query(collection(db, paths.tenantServiceAgreements(tenantId)), where("status", "==", "active"))).catch(() => null),
        getDocs(query(collection(db, paths.tenantJobs(tenantId)), where("kind", "==", "visit"))).catch(() => null),
      ]);
      if (dead) return;
      setFacts({
        builders: ids(projects),
        balance: ids(invoices, (d) => ((d.get("totalCents") as number) ?? 0) > ((d.get("amountPaidCents") as number) ?? 0)),
        agreement: ids(agreements),
        partial: ids(visits, (d) => d.get("status") === "scheduled" || d.get("status") === "in_progress"),
      });
    })();
    return () => {
      dead = true;
    };
  }, [tenantId, show, facts]);
  const [contact, setContact] = useState("all");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ displayName: "", company: "", email: "", phone: "", addressLine1: "", city: "", state: "", zip: "", leadSource: "", type: "" });
  // /customers?phone=… (the caller pop's "New customer"): open the form with the number filled in.
  const search = useSearch();
  useEffect(() => {
    const p = new URLSearchParams(search);
    const phone = p.get("phone");
    if (phone === null) return;
    setForm((f) => ({ ...f, phone, displayName: p.get("name") ?? f.displayName }));
    setShowCreate(true);
    window.history.replaceState(null, "", "/customers");
  }, [search]);
  const leadSources = useLeadSources(tenantId);
  const [loc, setLoc] = useState<Located>(null);
  const { hasIntegration } = useEntitlements();
  const qbConnected = hasIntegration("quickbooks-online") || hasIntegration("quickbooks-desktop");
  // QB-aware creation: as the name is typed, QuickBooks is searched (QBO
  // live, QBD via its nightly cache) so the office links the existing record
  // instead of minting a sync duplicate.
  const [qbMatches, setQbMatches] = useState<QbMatch[]>([]);
  const [qbSource, setQbSource] = useState<"qbo" | "qbd">("qbo");
  const [qbDismissed, setQbDismissed] = useState(false);
  const [pendingQbId, setPendingQbId] = useState<string | null>(null);
  const qbTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!tenantId || !qbConnected || !showCreate || qbDismissed || pendingQbId) return;
    const q = form.displayName.trim();
    if (q.length < 3) {
      setQbMatches([]);
      return;
    }
    if (qbTimer.current) clearTimeout(qbTimer.current);
    qbTimer.current = setTimeout(() => {
      void call<{ connected: boolean; source?: "qbo" | "qbd"; matches: QbMatch[] }>(
        "findQbCustomers",
        { tenantId, query: q },
        { mutation: false },
      )
        .then((res) => {
          setQbSource(res.source ?? "qbo");
          setQbMatches(res.connected ? res.matches : []);
        })
        .catch(() => setQbMatches([]));
    }, 500);
    return () => {
      if (qbTimer.current) clearTimeout(qbTimer.current);
    };
  }, [tenantId, qbConnected, showCreate, qbDismissed, pendingQbId, form.displayName]);

  async function createFromQb(m: QbMatch) {
    if (!tenantId) return;
    setBusy(true);
    try {
      const customer = {
        displayName: m.displayName,
        company: m.company,
        email: m.email,
        phone: m.phone,
        addressLine1: m.addressLine1,
        city: m.city,
        state: m.state,
        zip: m.zip,
        ...(form.type ? { type: form.type } : {}),
        tags: [],
        note: "",
        lat: null,
        lng: null,
        archivedAt: null,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        // The link that kills sync duplicates: invoices reuse this QB record.
        // (QBD syncs by FullName, so keeping the exact name is what links it;
        // the ListID is kept for the chip and re-prompt suppression.)
        ...(qbSource === "qbd"
          ? { qbd: { listId: m.qbId, fullName: m.displayName } }
          : { qbo: { id: m.qbId, lastSyncAt: null, lastError: null } }),
      };
      await addDoc(collection(db, paths.tenantCustomers(tenantId)), customer);
      closeCreate();
    } finally {
      setBusy(false);
    }
  }

  function editFirst(m: QbMatch) {
    setForm({
      displayName: m.displayName,
      company: m.company,
      email: m.email,
      phone: m.phone,
      addressLine1: m.addressLine1,
      city: m.city,
      state: m.state,
      zip: m.zip,
      leadSource: "",
      type: "",
    });
    setPendingQbId(m.qbId);
    setQbMatches([]);
  }

  function closeCreate() {
    setShowCreate(false);
    setForm({ displayName: "", company: "", email: "", phone: "", addressLine1: "", city: "", state: "", zip: "", leadSource: "", type: "" });
    setLoc(null);
    setQbMatches([]);
    setQbDismissed(false);
    setPendingQbId(null);
  }

  // Archived customers load only when someone asks to see them.
  useEffect(() => {
    if (!tenantId || status !== "archived") return;
    return onSnapshot(query(collection(db, paths.tenantCustomers(tenantId)), where("archivedAt", ">", 0)), (snap) =>
      setArchivedRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Customer) }))),
    );
  }, [tenantId, status]);

  // Cities by how many customers are in each (the filter's choices).
  const cities = useMemo(() => {
    const m = new Map<string, { label: string; n: number }>();
    for (const r of rows) {
      const c = r.city.trim();
      if (!c) continue;
      const key = c.toLowerCase();
      const e = m.get(key) ?? m.set(key, { label: c, n: 0 }).get(key)!;
      e.n += 1;
    }
    return [...m.entries()].sort((a, b) => b[1].n - a[1].n).slice(0, 60);
  }, [rows]);

  const isQb = (r: Row) => !!(r.qbo?.id || r.qbd?.listId);
  // Their type: what the office set, else a builder when they own a project, a business with a company name, else a homeowner.
  const typeOf = (r: Row) => customerTypeOf(r, !!facts?.builders.has(r.id));
  const showOk = (r: Row) =>
    show === "all" ||
    (show.startsWith("type:") ? typeOf(r) === show.slice(5) : show === "balance" ? !!facts?.balance.has(r.id) : show === "agreement" ? !!facts?.agreement.has(r.id) : !!facts?.partial.has(r.id));
  // Every text field counts: "lot 42" in an address or a note must hit.
  const view = useListView(status === "archived" ? archivedRows : rows, {
    text: (r) => [r.displayName, r.company, r.phone, r.email, r.addressLine1, r.city, r.state, r.zip, r.note, ...(r.tags ?? [])],
    sorts: {
      name: (a, b) => a.displayName.localeCompare(b.displayName, undefined, { numeric: true }),
      company: (a, b) => (a.company || "~").localeCompare(b.company || "~", undefined, { numeric: true }) || a.displayName.localeCompare(b.displayName, undefined, { numeric: true }),
      city: (a, b) => (a.city || "~").localeCompare(b.city || "~", undefined, { numeric: true }) || a.displayName.localeCompare(b.displayName, undefined, { numeric: true }),
      added: (a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0),
    },
    defaultSort: "name",
    pageSize: 50,
    filter: (r) =>
      showOk(r) &&
      (!city || r.city.trim().toLowerCase() === city) &&
      (qbFilter === "all" || (qbFilter === "linked" ? isQb(r) : !isQb(r))) &&
      (contact === "all" || (contact === "noemail" ? !r.email.trim() : contact === "nophone" ? !r.phone.trim() : !r.email.trim() && !r.phone.trim())),
  });
  const visible = view.filtered;
  const allPicked = view.page.length > 0 && view.page.every((r) => picked.has(r.id));

  async function setArchived(ids: string[], archived: boolean) {
    if (!tenantId) return;
    for (let i = 0; i < ids.length; i += 400) {
      const batch = writeBatch(db);
      for (const id of ids.slice(i, i + 400)) batch.update(doc(db, paths.tenantCustomer(tenantId, id)), { archivedAt: archived ? Date.now() : null, updatedAt: Date.now() });
      await batch.commit();
    }
    setPicked(new Set());
  }
  const exportRows = (list: Row[]) =>
    downloadCsv(
      "customers",
      ["Name", "Company", "Phone", "Email", "Address", "City", "State", "Zip"],
      list.map((r) => [r.displayName, r.company, r.phone, r.email, r.addressLine1, r.city, r.state, r.zip]),
    );

  async function createCustomer(e: FormEvent) {
    e.preventDefault();
    if (!tenantId) return;
    setBusy(true);
    try {
      const { type, ...rest } = form;
      const customer = {
        ...rest,
        ...(type ? { type } : {}),
        tags: [],
        note: "",
        lat: loc?.lat ?? null,
        lng: loc?.lng ?? null,
        archivedAt: null,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        // "Edit first" keeps the QuickBooks link through the edited save.
        ...(pendingQbId
          ? qbSource === "qbd"
            ? { qbd: { listId: pendingQbId, fullName: form.displayName } }
            : { qbo: { id: pendingQbId, lastSyncAt: null, lastError: null } }
          : {}),
      } satisfies Customer & { qbo?: object; qbd?: object };
      await addDoc(collection(db, paths.tenantCustomers(tenantId)), customer);
      closeCreate();
    } finally {
      setBusy(false);
    }
  }

  if (selectedId) {
    return <CustomerDetail customerId={selectedId} onBack={() => setSelectedId(null)} />;
  }

  return (
    <div className="mx-auto max-w-5xl space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-lg font-bold">
          Customers <span className="text-sm font-normal text-steel">{loaded ? rows.length.toLocaleString() : ""}</span>
        </h1>
        <div className="flex items-center gap-3">
          {can("customers.export") ? (
            <button className="shrink-0 text-xs text-steel hover:text-ink" onClick={() => exportRows(visible)}>
              Export {visible.length === view.total ? "all" : visible.length.toLocaleString()} to CSV
            </button>
          ) : null}
          <Button onClick={() => (showCreate ? closeCreate() : setShowCreate(true))}>
            {showCreate ? "Cancel" : "New customer"}
          </Button>
        </div>
      </div>

      {isAdmin ? (
        <div className="-mt-2 flex flex-wrap items-center gap-2 text-[11px] text-steel">
          <button type="button" className="hover:text-ink hover:underline" onClick={() => setTypesDraft((d) => (d === null ? customerTypes.join("\n") : null))}>
            {typesDraft === null ? "Customer types…" : "Close customer types"}
          </button>
          {typesNote ? <span>{typesNote}</span> : null}
        </div>
      ) : null}
      {typesDraft !== null ? (
        <Card className="space-y-2">
          <CardTitle>Customer types</CardTitle>
          <p className="text-xs text-steel">
            One per line. They drive the filter above, the Type on each customer, and who goes to Mailchimp. A customer with no type set counts as a builder when they own a project, a business when they have a
            company name, else a homeowner.
          </p>
          <textarea className="min-h-[6rem] w-full rounded-lg border border-steel/30 px-3 py-2 text-sm" value={typesDraft} onChange={(e) => setTypesDraft(e.target.value)} />
          <Button
            type="button"
            busy={busy}
            onClick={() =>
              void (async () => {
                setBusy(true);
                setTypesNote("");
                try {
                  const types = typesDraft.split("\n").map((t) => t.trim()).filter(Boolean);
                  await call("setCustomerTypes", { tenantId, types });
                  setTypesDraft(null);
                  setTypesNote("Saved.");
                } catch (err) {
                  setTypesNote((err as Error).message);
                } finally {
                  setBusy(false);
                }
              })()
            }
          >
            Save types
          </Button>
        </Card>
      ) : null}

      {showCreate ? (
        <Card>
          <CardTitle>New customer</CardTitle>
          <form onSubmit={createCustomer} className="grid grid-cols-2 gap-3">
            <div>
              <Label>Name</Label>
              <Input required value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
              {pendingQbId ? (
                <p className="mt-0.5 text-[11px] text-success">
                  Will link to the existing QuickBooks customer.{" "}
                  <button type="button" className="underline" onClick={() => setPendingQbId(null)}>
                    unlink
                  </button>
                </p>
              ) : null}
            </div>
            <div>
              <Label>Company</Label>
              <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
            </div>
            <div>
              <Label>Lead source</Label>
              <Select value={form.leadSource} onChange={(e) => setForm({ ...form, leadSource: e.target.value })}>
                <option value="">Not asked</option>
                {leadSources.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                <option value="">{form.company.trim() ? "Commercial (from the company name)" : "Homeowner"}</option>
                {customerTypes.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Address</Label>
              <AddressField
                fill="street"
                value={form.addressLine1}
                onChange={(addressLine1) => setForm((f) => ({ ...f, addressLine1 }))}
                located={loc}
                onLocate={setLoc}
                onPick={(m) => setForm((f) => ({ ...f, city: m.city || f.city, state: m.state || f.state, zip: m.zip || f.zip }))}
              />
            </div>
            <div>
              <Label>City</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>State</Label>
                <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
              </div>
              <div>
                <Label>Zip</Label>
                <Input value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} />
              </div>
            </div>
            {qbMatches.length > 0 ? (
              <div className="col-span-2 rounded-lg border border-brandorange/40 bg-cream p-3">
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-xs font-semibold text-brandorange-dark">
                    Already in QuickBooks{qbSource === "qbd" ? " Desktop" : ""}? {qbMatches.length} match
                    {qbMatches.length === 1 ? "" : "es"}
                  </span>
                  <button
                    type="button"
                    className="text-xs text-steel underline hover:text-ink"
                    onClick={() => {
                      setQbDismissed(true);
                      setQbMatches([]);
                    }}
                  >
                    Create new anyway
                  </button>
                </div>
                {qbMatches.map((m) => {
                  const linkedRow = rows.find(
                    (r) => (r.qbo?.id === m.qbId || r.qbd?.listId === m.qbId) && !r.archivedAt,
                  );
                  return (
                    <div key={m.qbId} className="flex items-center justify-between gap-2 border-b border-steel/10 py-1.5 last:border-0">
                      <div className="min-w-0 text-sm">
                        <span className="font-medium text-ink">{m.displayName}</span>
                        <span className="ml-1 text-xs text-steel">
                          {[m.company, m.phone, m.email, [m.addressLine1, m.city].filter(Boolean).join(", ")]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      </div>
                      {linkedRow ? (
                        <button
                          type="button"
                          className="shrink-0 text-xs text-steel underline hover:text-ink"
                          onClick={() => setSelectedId(linkedRow.id)}
                        >
                          already in Clevis — open
                        </button>
                      ) : (
                        <span className="flex shrink-0 gap-1.5">
                          <Button type="button" className="px-2 py-1 text-xs" busy={busy} onClick={() => void createFromQb(m)}>
                            Use as-is
                          </Button>
                          <Button type="button" variant="ghost" className="px-2 py-1 text-xs" onClick={() => editFirst(m)}>
                            Edit first
                          </Button>
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : null}
            <div className="col-span-2">
              <Button type="submit" busy={busy}>
                Create customer
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      <Card className="p-0">
        <div className="flex flex-wrap items-center gap-2 border-b border-steel/10 p-3">
          <SearchBox className="min-w-56 flex-1" value={view.query} onChange={view.setQuery} placeholder="Search name, company, phone, email, address, note…" />
          <Select className="w-auto py-1.5 text-sm" aria-label="City" value={city} onChange={(e) => setCity(e.target.value)}>
            <option value="">All cities</option>
            {cities.map(([key, c]) => (
              <option key={key} value={key}>
                {c.label} ({c.n.toLocaleString()})
              </option>
            ))}
          </Select>
          <Select className="w-auto py-1.5 text-sm" aria-label="Show" value={show} onChange={(e) => setShow(e.target.value)}>
            <option value="all">Everyone</option>
            {customerTypes.map((t) => (
              <option key={t} value={`type:${t}`}>
                {t}s
              </option>
            ))}
            <option value="balance">Owes a balance</option>
            <option value="agreement">Has a maintenance agreement</option>
            <option value="partial">Has a partly-done job</option>
          </Select>
          <Select className="w-auto py-1.5 text-sm" aria-label="QuickBooks" value={qbFilter} onChange={(e) => setQbFilter(e.target.value)}>
            <option value="all">QuickBooks: any</option>
            <option value="linked">Linked to QuickBooks</option>
            <option value="unlinked">Not in QuickBooks</option>
          </Select>
          <Select className="w-auto py-1.5 text-sm" aria-label="Contact info" value={contact} onChange={(e) => setContact(e.target.value)}>
            <option value="all">Any contact info</option>
            <option value="noemail">No email</option>
            <option value="nophone">No phone</option>
            <option value="none">No email or phone</option>
          </Select>
          <Select
            className="w-auto py-1.5 text-sm"
            aria-label="Status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as "active" | "archived");
              setPicked(new Set());
            }}
          >
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </Select>
        </div>

        <BulkBar count={picked.size} onClear={() => setPicked(new Set())}>
          {can("customers.export") ? (
            <button className="text-white/90 underline hover:text-white" onClick={() => exportRows(view.filtered.filter((r) => picked.has(r.id)))}>
              Export to CSV
            </button>
          ) : null}
          <span className="[&>span>span:first-child]:text-white">
            <ConfirmButton
              variant="secondary"
              className="border border-white/30 px-2.5 py-1 text-xs"
              confirm={status === "archived" ? `Restore ${picked.size}?` : `Archive ${picked.size}?`}
              confirmLabel={status === "archived" ? "Restore" : "Archive"}
              onConfirm={() => void setArchived([...picked], status !== "archived")}
            >
              {status === "archived" ? "Restore" : "Archive"}
            </ConfirmButton>
          </span>
        </BulkBar>

        <div className="hidden grid-cols-[24px_minmax(0,2.2fr)_minmax(0,1fr)_minmax(0,1.6fr)_minmax(0,1fr)_16px] items-center gap-3 border-b border-steel/10 px-4 py-2 sm:grid">
          <input
            type="checkbox"
            aria-label="Select this page"
            checked={allPicked}
            onChange={() =>
              setPicked((p) => {
                const n = new Set(p);
                for (const r of view.page) {
                  if (allPicked) n.delete(r.id);
                  else n.add(r.id);
                }
                return n;
              })
            }
          />
          <span className="flex gap-3">
            <SortHeader label="Name" k="name" view={view} />
            <SortHeader label="Company" k="company" view={view} />
          </span>
          <span className="text-[11px] font-semibold uppercase tracking-wide text-steel">Phone</span>
          <span className="text-[11px] font-semibold uppercase tracking-wide text-steel">Email</span>
          <span className="flex gap-3">
            <SortHeader label="City" k="city" view={view} />
            <SortHeader label="Newest" k="added" view={view} />
          </span>
          <span />
        </div>

        {view.page.map((r) => (
          <div
            key={r.id}
            className={cn(
              "grid cursor-pointer grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 border-b border-steel/10 px-4 py-1.5 text-sm last:border-0 hover:bg-cream/60 sm:grid-cols-[24px_minmax(0,2.2fr)_minmax(0,1fr)_minmax(0,1.6fr)_minmax(0,1fr)_16px] sm:gap-3",
              picked.has(r.id) && "bg-brandorange/5",
            )}
            onClick={() => setSelectedId(r.id)}
          >
            <input
              type="checkbox"
              aria-label={`Select ${r.displayName}`}
              checked={picked.has(r.id)}
              onClick={(e) => e.stopPropagation()}
              onChange={() =>
                setPicked((p) => {
                  const n = new Set(p);
                  if (n.has(r.id)) n.delete(r.id);
                  else n.add(r.id);
                  return n;
                })
              }
            />
            <span className="min-w-0">
              <span className="flex items-center gap-1.5">
                <span className="truncate font-medium text-ink">{r.displayName}</span>
                {isQb(r) ? <span className="shrink-0 rounded-full bg-success/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-success">QB</span> : null}
              </span>
              {r.company ? <span className="block truncate text-[11px] text-steel">{r.company}</span> : null}
              <span className="block truncate text-[11px] text-steel sm:hidden">{[r.phone, r.city].filter(Boolean).join(" · ")}</span>
            </span>
            <span className="hidden truncate text-steel sm:block">{r.phone || "—"}</span>
            <span className="hidden truncate text-steel sm:block">{r.email || "—"}</span>
            <span className="hidden truncate text-steel sm:block">{r.city || "—"}</span>
            <ChevronRight size={14} className="text-steel" />
          </div>
        ))}
        {view.filtered.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-steel">
            {!loaded ? "Loading customers…" : view.total === 0 ? (status === "archived" ? "No archived customers." : "No customers yet.") : "Nobody matches."}
          </div>
        ) : null}
        <ListFooter shown={view.page.length} matching={view.filtered.length} total={view.total} onMore={view.more} onAll={view.all} noun="customers" />
      </Card>
    </div>
  );
}
